import React, { useEffect, useState } from 'react';
import { Paper, Grid } from '@material-ui/core';
import TextHeader from 'components/CustomText/TextHeader';
import styled from 'styled-components';
import NumberListFormItem from './NumberListFormItem';
import CustomTextField from 'components/Input/CustomTextField';
import CustomTable from 'components/CustomTable';
import CustomButton from 'components/CustomInput/Button';
import PopupInput from 'components/Application/TeamMemberForm/PopupInput';
import useStudySiteOutOfWithHAData from 'hooks/tableData/useStudySiteOutOfWithHAData';
import useApplyingSiteData from 'hooks/tableData/useApplyingSiteData';
import useCollaboratingSiteData from 'hooks/tableData/useCollaboratingSiteData';
import CustomDatePicker from 'components/Input/CustomDatePicker';
import { addRemoveRequest, initializeTableData, produce, isHighlight as isHL } from 'utility/general';
import CustomOutlineSelect from 'components/Input/CustomOutlineSelect';
import DataListener from './DataListener';
import TableDialog from './TableDialog';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { SET_SITE_VALIDATION, SET_STUDY_VALIDATION, UPDATE_FORM_VALIDITY } from 'store/key';
import { getStore } from 'store';
import { APPLICANT, DRAFT, RETURNED } from 'utility/lookup';
import { commentStatusEnum } from 'constants/globalEnum';

const SPaperLayout = styled.div`
    padding: 10px;
`;

const SLayout = styled.div`
    padding: 10px;
`;

function OutlineOfApplicationFormLayout({
    tabType,
    defaultData = {},
    formId = -1,
    siteInfoId,
    disableOnPermission = false,
    disableTableOperations = false,
    onFormUpdate = (key, formPostData) => null,
    onTableUpdate = (tableKey, tableData) => null,
    applicationDetail,
    applicationCreateDtm,
    isAmendmentCreate,
    isAmendmentEdit,
    allComments,
    getCommentDetail,
    isEnableComment,
}) {
    const [postData, setPostData] = useState({});
    const [studyInfoValidation, setStudyInfoValidation] = useState({});
    const [siteInfoValidation, setSiteInfoValidation] = useState({});
    const user = useSelector((state) => state.user);
    const { statusId } = applicationDetail.status;

    const isAmendment = isAmendmentCreate || isAmendmentEdit;

    const validationKey = 'outlineOfApplication';

    const isStudyValid = useSelector(
        (state) => state.applicationDetailValid.isApplicationFormDetailValid?.[validationKey],
    );

    const isSiteValid = useSelector(
        (state) => state.applicationDetailValid.siteInfoValid?.[siteInfoId]?.[validationKey],
    );

    const isConductInPostData = useSelector(
        (state) => state.applicationDetailPostData?.postData?.applicationForm?.p1ConductedHa,
    );

    const dispatch = useDispatch();

    // update study-info validity to the reducer
    useEffect(() => {
        const validations = Object.values(studyInfoValidation);

        // data is not initialized
        if (validations.length === 0) {
            return;
        }

        const currentStatus = validations.every((item) => item);

        if (currentStatus !== isStudyValid) {
            dispatch({
                type: UPDATE_FORM_VALIDITY,
                payload: {
                    type: 'study',
                    applicationFormId: formId,
                    formKey: validationKey,
                    isValid: currentStatus,
                },
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studyInfoValidation]);

    // update site-info validity to the reducer
    useEffect(() => {
        const validations = Object.values(siteInfoValidation);

        // data is not initialized
        if (validations.length === 0) {
            return;
        }

        const currentStatus = validations.every((item) => item);

        if (currentStatus !== isSiteValid) {
            dispatch({
                type: UPDATE_FORM_VALIDITY,
                payload: {
                    type: 'site',
                    applicationFormId: formId,
                    siteInfoId: siteInfoId,
                    formKey: validationKey,
                    isValid: currentStatus,
                },
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteInfoValidation]);

    const updateStudyValidation = (name, isValidate) => {
        setStudyInfoValidation(
            produce((draft) => {
                if (name === 'p1ProposedStartDate' && isAmendment) {
                    draft[name] = true;
                } else {
                    draft[name] = isValidate;
                }
            }),
        );
    };

    const updateSiteValidation = (name, isValidate) => {
        setSiteInfoValidation(
            produce((draft) => {
                draft[name] = isValidate;
            }),
        );
    };

    // when siteInfoValidation change, update the reducer
    useEffect(() => {
        dispatch({
            type: SET_SITE_VALIDATION,
            payload: {
                siteInfoId,
                [validationKey]: siteInfoValidation,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteInfoValidation]);

    // when studyInfoValidation change, update the reducer
    useEffect(() => {
        dispatch({
            type: SET_STUDY_VALIDATION,
            payload: {
                [validationKey]: studyInfoValidation,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studyInfoValidation]);

    const {
        openPopup: openApplyingSitePopup,
        setOpenPopup: setApplyingSiteOpenPopup,
        columns: applyingSiteColumns,
        rows: applyingSiteRows,
        setRows: applyingSiteSetRows,
        defaultFormList: defaultApplyingSiteFormList,
        handleRowCreate: handleApplyingSiteRowCreate,
        onRecordUpdate: handleApplyingSiteRecordUpdate,
        handleEditClick: handleApplyingSiteEditClick,
        handleDelete: handleApplyingSiteDelete,
        onEditSubmit: onEditApplyingSiteSubmit,
        isEdit: isApplyingSiteEdit,
        emptyTableData: onApplyingSiteDataEmpty,
        recoverDataCreation: recoverApplyingSiteDataCreation,
    } = useApplyingSiteData({ formId, siteInfoId });

    const {
        openPopup: openCollaboratingSitePopup,
        setOpenPopup: setCollaboratingSiteOpenPopup,
        columns: collaboratingSiteColumns,
        rows: collaboratingSiteRows,
        setRows: setCollaboratingSiteRows,
        defaultFormList: defaultCollaboratingSiteFormList,
        handleRowCreate: handleCollaboratingSiteRowCreate,
        onRecordUpdate: handleCollaboratingSiteRecordUpdate,
        handleEditClick: handleCollaboratingSiteEditClick,
        handleDelete: handleCollaboratingSiteDelete,
        onEditSubmit: onEditCollaboratingSiteSubmit,
        onDataEmpty: onCollaboratingSiteDataEmpty,
        recoverDataCreation: recoverCollaboratingSiteDataCreation,
        isEdit: isCollaboratingSiteEdit,
        removeAllTempData: removeAllCollaboratingTempData,
    } = useCollaboratingSiteData({ formId });

    const {
        openPopup: openStudySiteWithHAPopup,
        setOpenPopup: setStudySiteWithHAOpenPopup,
        columns: studySiteWithHAColumns,
        rows: studySiteWithHARows,
        setRows: setStudySiteWithHARows,
        defaultFormList: defaultStudySiteWithHAFormList,
        selectedRow: selectedStudySiteWithHARow,
        handleRowCreate: handleStudySiteWithHARowCreate,
        onRecordUpdate: handleStudySiteWithHARecordUpdate,
        handleEditClick: handleStudySiteWithHAEditClick,
        handleDelete: handleStudySiteWithHADelete,
        onEditSubmit: onEditStudySiteWithHASubmit,
        removeAllTempData: removeAllStudySiteOutOfWithHATempData,
        onDataEmpty: onStudySiteOutOfWithHADataEmpty,
        recoverDataCreation: recoverStudySiteOutOfWithHADataCreation,
    } = useStudySiteOutOfWithHAData({ formId });

    // initialize the site-info postData if edit data exist
    useEffect(() => {
        if (tabType === 'hospital') {
            const newData = initializeTableData({
                tableKey: 'studySiteApply',
                currentTableData: applyingSiteRows,
                siteInfoId,
            });

            applyingSiteSetRows(newData);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // initialize the study-info postData if edit data exist
    useEffect(() => {
        if (tabType === 'info') {
            const { applicationDetailPostData } = getStore().getState();
            const postData = applicationDetailPostData.postData?.applicationForm ?? {};
            setPostData(postData);

            const studySiteCollabData = initializeTableData({
                tableKey: 'studySiteCollab',
                currentTableData: collaboratingSiteRows,
            });

            setCollaboratingSiteRows(studySiteCollabData);

            const studySiteOutOfHAData = initializeTableData({
                tableKey: 'studySiteOutOfHA',
                currentTableData: studySiteWithHARows,
            });

            setStudySiteWithHARows(studySiteOutOfHAData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        checkSiteFormValid();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabType]);

    const shouldDisplayTips = () => {
        if ((statusId === DRAFT || statusId === RETURNED) && user.role === APPLICANT) {
        // if (statusId === DRAFT && user.role === APPLICANT) {
            return true;
        }

        return false;
    };

    const checkSiteFormValid = () => {
        if (tabType === 'hospital') {
            const isValid = true;

            if (isValid) {
                dispatch({
                    type: UPDATE_FORM_VALIDITY,
                    payload: {
                        type: 'site',
                        applicationFormId: formId,
                        siteInfoId: siteInfoId,
                        formKey: validationKey,
                        isValid: true,
                    },
                });
            }
        }
    };

    const isConduct = () => {
        const valueOfApplication = applicationDetail?.applicationForm?.p1ConductedHa;
        const valueOfPostData = isConductInPostData;

        if (valueOfPostData !== undefined && valueOfPostData !== null) {
            return valueOfPostData;
        } else {
            return !!valueOfApplication;
        }
    };

    const getData = (key) => {
        if (postData[key] === null) {
            return postData[key];
        }

        return postData[key] ?? defaultData[key];
    };

    const onFieldChange = (e) => {
        const { name, value } = e.target;

        setPostData((prevData) => {
            return {
                ...prevData,
                [name]: value,
            };
        });

        onFormUpdate(name, value);
    };

    const isHighlight = (keys, istable = false) => {
        return isHL(applicationDetail, siteInfoId, formId, tabType, keys, istable);
    };

    const updateAllSitesValidation = (isConduct) => {
        const sites = applicationDetail.siteInfo;

        if (isConduct) {
            // check table data, if no data exist, update site info validation
            sites.forEach((item) => {
                const siteInfoId = item.id;
                const hasTableDataExist = applicationDetail.studySiteApply.some((item) => item.id === siteInfoId);
                dispatch({
                    type: UPDATE_FORM_VALIDITY,
                    payload: {
                        type: 'site',
                        applicationFormId: formId,
                        siteInfoId: siteInfoId,
                        formKey: validationKey,
                        isValid: hasTableDataExist,
                    },
                });
            });
        } else {
            sites.forEach((item) => {
                const siteInfoId = item.id;

                dispatch({
                    type: UPDATE_FORM_VALIDITY,
                    payload: {
                        type: 'site',
                        applicationFormId: formId,
                        siteInfoId: siteInfoId,
                        formKey: validationKey,
                        isValid: true,
                    },
                });
            });
        }
    };

    return (
        <SPaperLayout>
            <DataListener
                onPostDataReset={() => {
                    setPostData({});
                    removeAllStudySiteOutOfWithHATempData();
                    removeAllCollaboratingTempData();
                    removeAllStudySiteOutOfWithHATempData();
                }}
            />
            {openApplyingSitePopup && (
                <TableDialog
                    defaultFormList={defaultApplyingSiteFormList}
                    title={'Applying site(s) in HA'}
                    onSubmit={() => {
                        if (isApplyingSiteEdit) {
                            onEditApplyingSiteSubmit();
                        } else {
                            handleApplyingSiteRecordUpdate();
                        }
                    }}
                    onClose={() => setApplyingSiteOpenPopup(false)}
                />
            )}

            {openCollaboratingSitePopup && (
                <TableDialog
                    defaultFormList={defaultCollaboratingSiteFormList}
                    title={'Collaborating site(s) in HA'}
                    onSubmit={() => {
                        if (isCollaboratingSiteEdit) {
                            onEditCollaboratingSiteSubmit();
                        } else {
                            handleCollaboratingSiteRecordUpdate();
                        }
                    }}
                    onClose={() => setCollaboratingSiteOpenPopup(false)}
                />
            )}

            {openStudySiteWithHAPopup && (
                <PopupInput
                    title={'Study at sites out-of-HA'}
                    height="420px"
                    width="600px"
                    alignItems={'center'}
                    isShow={openStudySiteWithHAPopup}
                    defaultFormList={defaultStudySiteWithHAFormList}
                    selectedRow={selectedStudySiteWithHARow}
                    editRow={onEditStudySiteWithHASubmit}
                    setOpenPopup={setStudySiteWithHAOpenPopup}
                    tableList={studySiteWithHARows}
                    onSubmit={handleStudySiteWithHARecordUpdate}
                />
            )}

            <Paper>
                <SLayout>
                    <TextHeader underline italic>
                        PART I: OUTLINE OF APPLICATION
                    </TextHeader>
                    {tabType === 'hospital' ? (
                        <>
                            <NumberListFormItem number={'2'} bold label={'Study Sites(s)'} />
                            <NumberListFormItem number={'2.3'} label={'Study sites'} />
                            <NumberListFormItem
                                number={'2.3.1'}
                                level={2}
                                displayContentNextLine
                                labelTooltip={
                                    'Sites for approval in this application under the PI stated in Q.P1 above.'
                                }
                                label={'Applying site(s) in HA'}
                                highlight={isHighlight(['studySiteApply'], true)}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '2.3.1 Applying site(s) in HA', 
                                    commentStatus: null,
                                    commentField: 'studySiteApply',
                                    commentDetail: getCommentDetail("studySiteApply", allComments),
                                }}
                            >
                                <Grid container>
                                    <CustomButton
                                        allowAction={isConduct()}
                                        style={{ marginBottom: 8 }}
                                        onClick={handleApplyingSiteRowCreate}
                                        disableOnPermission={disableOnPermission || disableTableOperations}
                                    >
                                        Add
                                    </CustomButton>
                                    <CustomTable
                                        disableOnPermission={disableOnPermission || disableTableOperations}
                                        allowAction={isConduct()}
                                        fullWidthTable
                                        rows={applyingSiteRows}
                                        columns={applyingSiteColumns}
                                        isEditAllow
                                        isDeleteAllow
                                        name={'studySiteApply'}
                                        onAppFieldValidate={updateSiteValidation}
                                        onDelete={handleApplyingSiteDelete}
                                        onEdit={handleApplyingSiteEditClick}
                                        applicationCreateDtm={applicationCreateDtm}
                                        disableOldDataDelete={isAmendment}
                                    />
                                </Grid>
                            </NumberListFormItem>
                        </>
                    ) : (
                        <>
                            <NumberListFormItem 
                                isEnableComment={false} 
                                number={'1'} 
                                bold 
                                label={'Name of Study'} 
                                fontSize={16} 
                            />
                            <NumberListFormItem
                                number={'1.1'}
                                highlight={isHighlight(['p1ScientificTitle'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '1.1 Scientific Title', 
                                    commentStatus: null,
                                    commentField: 'p1ScientificTitle',
                                    commentDetail: getCommentDetail("p1ScientificTitle", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    debounceChange
                                    label={'Scientific Title'}
                                    value={getData('p1ScientificTitle')}
                                    name={'p1ScientificTitle'}
                                    onChange={onFieldChange}
                                    onAppFieldValidate={updateStudyValidation}
                                    maxLength={500}
                                    isTextArea
                                    textAreaMinRow={3}
                                    canPressEnter={false}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'1.1.1'}
                                level={2}
                                highlight={isHighlight(['p1ShortTitle'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '1.1.1 Short Title', 
                                    commentStatus: null,
                                    commentField: 'p1ShortTitle',
                                    commentDetail: getCommentDetail("p1ShortTitle", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    debounceChange
                                    label={'Short Title (for lay public / easy quote)'}
                                    value={getData('p1ShortTitle')}
                                    name={'p1ShortTitle'}
                                    onAppFieldValidate={updateStudyValidation}
                                    onChange={onFieldChange}
                                    maxLength={255}
                                    isTextArea
                                    textAreaMinRow={3}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'1.1.2'}
                                level={2}
                                highlight={isHighlight(['p1KeyWords'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '1.1.2 Key Words', 
                                    commentStatus: null,
                                    commentField: 'p1KeyWords',
                                    commentDetail: getCommentDetail("p1KeyWords", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    debounceChange
                                    label={'Key Words (for search purpose, e.g. disease name, drug name, etc.)'}
                                    value={getData('p1KeyWords')}
                                    name={'p1KeyWords'}
                                    onAppFieldValidate={updateStudyValidation}
                                    onChange={onFieldChange}
                                    maxLength={255}
                                    isTextArea
                                    textAreaMinRow={3}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem number={'1.2'} highlight={isHighlight(['p1ResProNum'])} 
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '1.2 Research protocol number', 
                                    commentStatus: null,
                                    commentField: 'p1ResProNum',
                                    commentDetail: getCommentDetail("p1ResProNum", allComments),
                                }}>
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    debounceChange
                                    value={getData('p1ResProNum')}
                                    name={'p1ResProNum'}
                                    onChange={onFieldChange}
                                    label={'Research protocol number'}
                                    isValidValue={(isValid) => {
                                        updateStudyValidation('p1ResProNum', isValid);
                                    }}
                                    // onAppFieldValidate={updateStudyValidation} //optional field
                                    maxLength={255}
                                    tooltip={
                                        'Version number is not regarded as Protocol Number. State “N/A” if it is not applicable.'
                                    }
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'2'}
                                label={'Study Site(s)'}
                                fontSize={16}
                                bold
                                style={{ marginTop: 30 }}
                            />
                            <NumberListFormItem
                                number={'2.1'}
                                label={'Is this a local or international trial?'}
                                highlight={isHighlight(['p1LocalInternational'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '2.1 Is this a local or international trial?', 
                                    commentStatus: null,
                                    commentField: 'p1LocalInternational',
                                    commentDetail: getCommentDetail("p1LocalInternational", allComments),
                                }}
                            >
                                <CustomOutlineSelect
                                    disableOnPermission={disableOnPermission}
                                    style={{ paddingLeft: 15, width: 300 }}
                                    name={'p1LocalInternational'}
                                    value={getData('p1LocalInternational')}
                                    onAppFieldValidate={updateStudyValidation}
                                    onChange={onFieldChange}
                                    choices={[
                                        { key: 'L', display: 'Local' },
                                        { key: 'I', display: 'Local-Initiated International' },
                                        { key: 'O', display: 'Overseas-Initiated International' },
                                    ]}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'2.2'}
                                label={'Will the study be conducted in HA hospitals/institutions?'}
                                showTrueFalse
                                disableOnPermission={disableOnPermission}
                                trueFalseValue={getData('p1ConductedHa')}
                                onAppFieldValidate={updateStudyValidation}
                                name={'p1ConductedHa'}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '2.2 Will the study be conducted in HA hospitals/institutions?', 
                                    commentStatus: null,
                                    commentField: 'p1ConductedHa',
                                    commentDetail: getCommentDetail("p1ConductedHa", allComments),
                                }}
                                onTrueFalseSelected={(e) => {
                                    onFieldChange(e);

                                    const sites = applicationDetail.siteInfo;

                                    if (e.target.value === false) {
                                        onCollaboratingSiteDataEmpty();

                                        sites.forEach((item) => {
                                            onApplyingSiteDataEmpty(item.id);
                                        });

                                        updateAllSitesValidation(false);
                                    } else {
                                        recoverCollaboratingSiteDataCreation();

                                        sites.forEach((item) => {
                                            recoverApplyingSiteDataCreation(item.id);
                                        });

                                        updateAllSitesValidation(true);
                                    }
                                }}
                                highlight={isHighlight(['p1ConductedHa'])}
                            />
                            <NumberListFormItem number={'2.3'} label={'Study sites'} />
                            <NumberListFormItem
                                number={'2.3.1'}
                                level={2}
                                displayContentNextLine
                                label={'Applying site(s) in HA'}
                                showDisableMessage
                                highlight={isHighlight(['XXX'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '2.3.1 Applying site(s) in HA', 
                                    commentStatus: null,
                                    commentField: 'studySiteApply',
                                    commentDetail: getCommentDetail("studySiteApply", allComments),
                                }}
                            >
                                <Grid container>
                                    <CustomButton disableOnPermission style={{ marginBottom: 8 }}>
                                        Add
                                    </CustomButton>
                                    <CustomTable
                                        disableOnPermission={disableOnPermission}
                                        fullWidthTable
                                        // rows={applyingSiteRows}
                                        rows={[]}
                                        columns={applyingSiteColumns}
                                        isEditAllow
                                        isDeleteAllow
                                        disableDelete={isAmendmentCreate || isAmendmentEdit}
                                        applicationCreateDtm={applicationCreateDtm}
                                        // onDelete={handleApplyingSiteDelete}
                                        // onEdit={handleApplyingSiteEditClick}
                                    />
                                </Grid>
                            </NumberListFormItem>
                            <NumberListFormItem
                                number={'2.3.2'}
                                level={2}
                                labelTooltip={
                                    'Collaborating sites in HA but not applying for ethics approval in this application.'
                                }
                                label={'Collaborating site(s) in HA'}
                                highlight={isHighlight(['studySiteCollab'], true)}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '2.3.2 Collaborating site(s) in HA', 
                                    commentStatus: null,
                                    commentField: 'studySiteCollab',
                                    commentDetail: getCommentDetail("studySiteCollab", allComments),
                                }}
                            />

                            <NumberListFormItem level={2}>
                                <Grid container>
                                    <CustomButton
                                        disableOnPermission={disableOnPermission || disableTableOperations}
                                        style={{ marginBottom: 8 }}
                                        allowAction={getData('p1ConductedHa') === true}
                                        onClick={handleCollaboratingSiteRowCreate}
                                    >
                                        Add
                                    </CustomButton>
                                    <CustomTable
                                        disableOnPermission={disableOnPermission || disableTableOperations}
                                        fullWidthTable
                                        rows={collaboratingSiteRows}
                                        columns={collaboratingSiteColumns}
                                        isEditAllow
                                        isDeleteAllow
                                        name={'studySiteCollab'}
                                        disableOldDataDelete={isAmendment}
                                        // onAppFieldValidate={updateStudyValidation} // optional by client request
                                        allowAction={getData('p1ConductedHa') === true}
                                        onDelete={handleCollaboratingSiteDelete}
                                        onEdit={handleCollaboratingSiteEditClick}
                                        applicationCreateDtm={applicationCreateDtm}
                                    />
                                </Grid>
                            </NumberListFormItem>
                            <NumberListFormItem
                                disableOnPermission={disableOnPermission}
                                number={'2.3.3'}
                                level={2}
                                label={'Will the study be conducted out-of-HA?'}
                                showTrueFalse
                                trueFalseValue={getData('p1ConductedOutOfHa')}
                                onAppFieldValidate={updateStudyValidation}
                                name={'p1ConductedOutOfHa'}
                                onTrueFalseSelected={(e) => {
                                    onFieldChange(e);

                                    if (e.target.value) {
                                        recoverStudySiteOutOfWithHADataCreation();
                                    } else {
                                        onStudySiteOutOfWithHADataEmpty();
                                    }
                                }}
                                highlight={isHighlight(['studySiteOutOfHA'], true)}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '2.3.3 Will the study be conducted out-of-HA?', 
                                    commentStatus: null,
                                    commentField: 'studySiteOutOfHA',
                                    commentDetail: getCommentDetail("studySiteOutOfHA", allComments),
                                }}
                            />

                            <NumberListFormItem level={2}>
                                <Grid container>
                                    <CustomButton
                                        disableOnPermission={disableOnPermission || disableTableOperations}
                                        allowAction={getData('p1ConductedOutOfHa') === true}
                                        style={{ marginBottom: 8 }}
                                        onClick={handleStudySiteWithHARowCreate}
                                    >
                                        Add
                                    </CustomButton>
                                    <CustomTable
                                        disableOnPermission={disableOnPermission || disableTableOperations}
                                        fullWidthTable
                                        rows={studySiteWithHARows}
                                        columns={studySiteWithHAColumns}
                                        isEditAllow
                                        isDeleteAllow
                                        disableOldDataDelete={isAmendment}
                                        name={'studySiteOutOfHA'}
                                        onAppFieldValidate={updateStudyValidation}
                                        onDelete={handleStudySiteWithHADelete}
                                        allowAction={getData('p1ConductedOutOfHa') === true}
                                        onEdit={handleStudySiteWithHAEditClick}
                                        applicationCreateDtm={applicationCreateDtm}
                                    />
                                </Grid>
                            </NumberListFormItem>

                            <NumberListFormItem
                                level={2}
                                label={
                                    'Please be reminded to seek ethics approval(s) from respective authority(ies) where appropriate.'
                                }
                                italic
                            />
                            <NumberListFormItem
                                number={'3'}
                                level={1}
                                bold
                                fontSize={16}
                                style={{ marginTop: 30 }}
                                label={'Clinical Research Ethics Review for Cross-cluster Study'}
                            />
                            <NumberListFormItem
                                disableOnPermission={disableOnPermission}
                                number={'3.1'}
                                label={'Has the application been reviewed by another Cluster REC/IRB?'}
                                showTrueFalse
                                trueFalseValue={getData('p1ReviewByRecIrb')}
                                onAppFieldValidate={updateStudyValidation}
                                name={'p1ReviewByRecIrb'}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p1ReviewByRecIrb'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '3.1 Has the application been reviewed by another Cluster REC/IRB?', 
                                    commentStatus: null,
                                    commentField: 'p1ReviewByRecIrb',
                                    commentDetail: getCommentDetail("p1ReviewByRecIrb", allComments),
                                }}
                            />
                            <NumberListFormItem
                                number={'3.1.1'}
                                level={2}
                                label={'What is REC/IRB decision?'}
                                highlight={isHighlight(['p1RecIrbDecision'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '3.1.1 What is REC/IRB decision?', 
                                    commentStatus: null,
                                    commentField: 'p1RecIrbDecision',
                                    commentDetail: getCommentDetail("p1RecIrbDecision", allComments),
                                }}
                            >
                                <CustomOutlineSelect
                                    disableOnPermission={disableOnPermission}
                                    allowAction={getData('p1ReviewByRecIrb') === true}
                                    updateApplicationFormOnDisableChange={formId}
                                    style={{ paddingLeft: 15, width: 200 }}
                                    placeholder={'Choose an Item'}
                                    onAppFieldValidate={updateStudyValidation}
                                    choices={[
                                        { key: 'A', display: 'Approved' },
                                        { key: 'D', display: 'Disapproved' },
                                        { key: 'R', display: 'Under review' },
                                        { key: 'U', display: 'Unknown' },
                                    ]}
                                    name={'p1RecIrbDecision'}
                                    value={getData('p1RecIrbDecision')}
                                    onChange={onFieldChange}
                                />
                            </NumberListFormItem>
                            <NumberListFormItem
                                level={2}
                                label={'(Please attach the supporting document in Section 32)'}
                            />
                            <NumberListFormItem
                                label={'Timetable'}
                                number={'4'}
                                bold
                                fontSize={16}
                                style={{ marginTop: 30 }}
                            />

                            <NumberListFormItem
                                number={'4.1'}
                                label={'Proposed study start date'}
                                displayContentNextLine
                                labelTooltip="Suggest to be the date after the coming panel meeting."
                                highlight={isHighlight(['p1ProposedStartDate'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '4.1 Proposed study start date', 
                                    commentStatus: null,
                                    commentField: 'p1ProposedStartDate',
                                    commentDetail: getCommentDetail("p1ProposedStartDate", allComments),
                                }}
                            >
                                <CustomDatePicker
                                    disableOnPermission={disableOnPermission}
                                    name={'p1ProposedStartDate'}
                                    value={getData('p1ProposedStartDate') ?? null}
                                    minDate={isAmendment ? null : new Date().getTime() + 60 * 60 * 24 * 1000 * 1}
                                    customValidationMessage={'Date should be later than today'}
                                    hiddenValidationTips={!shouldDisplayTips()}
                                    onAppFieldValidate={updateStudyValidation}
                                    handleChangeDate={(e) => {
                                        onFieldChange(e);

                                        // clear value of p1SubmissionDate and p1ProposedEndDate
                                        onFieldChange({
                                            target: {
                                                name: 'p1ProposedEndDate',
                                                value: null,
                                            },
                                        });

                                        addRemoveRequest({
                                            applicationFormId: formId,
                                            name: 'p1ProposedEndDate',
                                        });
                                        onFieldChange({
                                            target: {
                                                name: 'p1SubmissionDate',
                                                value: null,
                                            },
                                        });

                                        addRemoveRequest({
                                            applicationFormId: formId,
                                            name: 'p1SubmissionDate',
                                        });
                                    }}
                                    style={{ width: 300 }}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                label={
                                    'Proposed study end date / date of last follow-up of all recruited subjects, whichever is later'
                                }
                                displayContentNextLine
                                number={'4.2'}
                                highlight={isHighlight(['p1ProposedEndDate'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '4.2 Proposed study end date / date of last follow-up of all recruited subjects, whichever is later', 
                                    commentStatus: null,
                                    commentField: 'p1ProposedEndDate',
                                    commentDetail: getCommentDetail("p1ProposedEndDate", allComments),
                                }}
                            >
                                <CustomDatePicker
                                    disableOnPermission={disableOnPermission}
                                    name={'p1ProposedEndDate'}
                                    value={getData('p1ProposedEndDate') ?? null}
                                    disabled={getData('p1ProposedStartDate') === null}
                                    minDate={
                                        new Date(getData('p1ProposedStartDate')).getTime() + 60 * 60 * 24 * 1000 * 1
                                    }
                                    maxDate={new Date(
                                        getData('p1SubmissionDate') ?? new Date().getTime() * 2,
                                    ).getTime()}
                                    test
                                    onAppFieldValidate={updateStudyValidation}
                                    handleChangeDate={onFieldChange}
                                    updateApplicationFormOnDisableChange={formId}
                                    style={{ width: 300 }}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'4.3'}
                                label={'Tentative final report submission date to CIRB/CREC/IRB'}
                                displayContentNextLine
                                highlight={isHighlight(['p1SubmissionDate'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '4.3 Tentative final report submission date to CIRB/CREC/IRB', 
                                    commentStatus: null,
                                    commentField: 'p1SubmissionDate',
                                    commentDetail: getCommentDetail("p1SubmissionDate", allComments),
                                }}
                            >
                                <CustomDatePicker
                                    disableOnPermission={disableOnPermission}
                                    name={'p1SubmissionDate'}
                                    value={getData('p1SubmissionDate') ?? null}
                                    handleChangeDate={onFieldChange}
                                    updateApplicationFormOnDisableChange={formId}
                                    onAppFieldValidate={updateStudyValidation}
                                    minDate={new Date(getData('p1ProposedEndDate')).getTime()}
                                    disabled={
                                        getData('p1ProposedStartDate') === null || getData('p1ProposedEndDate') === null
                                    }
                                    style={{ width: 300 }}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                labelTooltip={
                                    'A brief introduction to study design. Use language that can be understood by laypersons. Technical terms are not recommended and no referral to protocols/other study documents.'
                                }
                                number={'5'}
                                bold
                                fontSize={16}
                                style={{ marginTop: 30 }}
                                label={'Brief Summary of Study'}
                                displayContentNextLine
                                highlight={isHighlight(['p1BriefSummary'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '5 A brief introduction to study design. Use language that can be understood by laypersons. Technical terms are not recommended and no referral to protocols/other study documents.', 
                                    commentStatus: null,
                                    commentField: 'p1BriefSummary',
                                    commentDetail: getCommentDetail("p1BriefSummary", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    debounceChange
                                    value={getData('p1BriefSummary')}
                                    name={'p1BriefSummary'}
                                    onChange={onFieldChange}
                                    onAppFieldValidate={updateStudyValidation}
                                    maxLength={6000}
                                    isTextArea
                                    textAreaMinRow={12}
                                />
                            </NumberListFormItem>
                        </>
                    )}
                </SLayout>
            </Paper>
        </SPaperLayout>
    );
}

export default OutlineOfApplicationFormLayout;
